import { useContext } from "react";
import { ConfigProvider, ConfigProviderProps } from "antd";
import {
  useTooltipStyle,
  useInputStyle,
  useButtonStyle,
  useSelectStyle,
  useDatepickerStyle,
  useTableStyle,
  useCheckboxStyle,
  useSwitchStyle,
  useRadioStyle,
  usePaginationStyle,
  useBreadcrumbStyle,
  useTagStyle,
  useAlertStyle,
  useDrawerStyle,
  useBadgeStyle,
  useTypographyStyle,
  useFlexStyle,
  useGridStyle,
  useLayoutStyle,
  useCarouselStyle,
  useMenuStyle,
  useRateStyle,
  useCollapseStyle,
  useAvatarStyle,
  useFormStyle,
  useCardStyle,
  useUploadStyle,
  useModalStyle,
  useTabsStyle,
  useImageStyle,
  useSkeletonStyle,
  useSpinStyle,
  useStepsStyle,
  useProgressStyle,
  useDividerStyle,
  useResultStyle,
  useSpaceStyle,
  useCalendarStyle,
  useAffixStyle,
} from "@/base/components/styles";
import { THEME_PREFIX } from "@/base/constant";
import { CloseIcon, ChevronRightIcon } from "@/base/static/index";

export interface ThemeCSOStylesProviderProps extends ConfigProviderProps {
  children: React.ReactNode | React.ReactNode[];
}

export const ThemeCSOStylesProvider = ({
  children,
  ...props
}: ThemeCSOStylesProviderProps) => {
  const { getPrefixCls } = useContext(ConfigProvider.ConfigContext);

  useInputStyle(getPrefixCls("input"));
  useDatepickerStyle(getPrefixCls("datepicker"));
  useTooltipStyle(getPrefixCls("tooltip"));
  useTableStyle(getPrefixCls("table"));
  useButtonStyle(getPrefixCls("button"));
  useSelectStyle(getPrefixCls("select"));
  useCheckboxStyle(getPrefixCls("checkbox"));
  useSwitchStyle(getPrefixCls("switch"));
  useRadioStyle(getPrefixCls("radio"));
  usePaginationStyle(getPrefixCls("pagination"));
  useBreadcrumbStyle(getPrefixCls("breadcrumb"));
  useTagStyle(getPrefixCls("tag"));
  useAlertStyle(getPrefixCls("alert"));
  useDrawerStyle(getPrefixCls("drawer"));
  useBadgeStyle(getPrefixCls("badge"));
  useTypographyStyle(getPrefixCls("typography"));
  useFlexStyle(getPrefixCls("flex"));
  useGridStyle(getPrefixCls("grid"));
  useLayoutStyle(getPrefixCls("layout"));
  useCarouselStyle(getPrefixCls("carousel"));
  useMenuStyle(getPrefixCls("menu"));
  useRateStyle(getPrefixCls("rate"));
  useCollapseStyle(getPrefixCls("collapse"));
  useAvatarStyle(getPrefixCls("avatar"));
  useFormStyle(getPrefixCls("form"));
  useCardStyle(getPrefixCls("card"));
  useUploadStyle(getPrefixCls("upload"));
  useModalStyle(getPrefixCls("modal"));
  useTabsStyle(getPrefixCls("tabs"));
  useImageStyle(getPrefixCls("image"));
  useSkeletonStyle(getPrefixCls("skeleton"));
  useSpinStyle(getPrefixCls("spin"));
  useStepsStyle(getPrefixCls("steps"));
  useProgressStyle(getPrefixCls("progress"));
  useDividerStyle(getPrefixCls("divider"));
  useResultStyle(getPrefixCls("result"));
  useSpaceStyle(getPrefixCls("space"));
  useCalendarStyle(getPrefixCls("calendar"));
  useAffixStyle(getPrefixCls("affix"));

  return (
    <ConfigProvider
      button={{ className: THEME_PREFIX }}
      input={{ className: THEME_PREFIX }}
      datePicker={{ className: THEME_PREFIX }}
      tooltip={{ className: THEME_PREFIX }}
      table={{ className: THEME_PREFIX }}
      select={{ className: THEME_PREFIX }}
      checkbox={{ className: THEME_PREFIX }}
      switch={{ className: THEME_PREFIX }}
      radio={{ className: THEME_PREFIX }}
      pagination={{ className: THEME_PREFIX }}
      timePicker={{ className: THEME_PREFIX }}
      breadcrumb={{ className: THEME_PREFIX }}
      badge={{ className: THEME_PREFIX }}
      typography={{ className: THEME_PREFIX }}
      tag={{
        className: THEME_PREFIX,
        closeIcon: <CloseIcon size={20} />,
      }}
      alert={{ className: THEME_PREFIX }}
      drawer={{
        className: THEME_PREFIX,
        closeIcon: <CloseIcon size={20} />,
      }}
      flex={{ className: THEME_PREFIX }}
      layout={{ className: THEME_PREFIX }}
      carousel={{ className: THEME_PREFIX }}
      menu={{ className: THEME_PREFIX }}
      popover={{ className: THEME_PREFIX }}
      rate={{ className: THEME_PREFIX }}
      collapse={{
        className: THEME_PREFIX,
        expandIcon: ({ isActive }) => (
          <ChevronRightIcon size={20} rotate={isActive ? 90 : 0} />
        ),
      }}
      avatar={{ className: THEME_PREFIX }}
      form={{ className: THEME_PREFIX }}
      card={{ className: THEME_PREFIX, variant: "borderless" }}
      upload={{ className: THEME_PREFIX }}
      modal={{
        className: THEME_PREFIX,
        closeIcon: <CloseIcon size={20} />,
      }}
      tabs={{ className: THEME_PREFIX }}
      image={{ className: THEME_PREFIX }}
      skeleton={{ className: THEME_PREFIX }}
      spin={{ className: THEME_PREFIX }}
      steps={{ className: THEME_PREFIX }}
      progress={{ className: THEME_PREFIX }}
      result={{ className: THEME_PREFIX }}
      space={{ className: THEME_PREFIX }}
      calendar={{ className: THEME_PREFIX }}
      {...props}
    >
      {children}
    </ConfigProvider>
  );
};
